import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lesson1Gravity } from "@/lessons/Lesson1Gravity";
import { Lesson2Elasticity } from "@/lessons/Lesson2Elasticity";
import { Lesson3Collisions } from "@/lessons/Lesson3Collisions";
import { Lesson4Sandbox } from "@/lessons/Lesson4Sandbox";
import { Lesson5Energy } from "@/lessons/Lesson5Energy";
import "./index.css";

interface Lesson {
	id: number;
	title: string;
	subtitle: string;
	icon: string;
	color: string;
	description: string;
	component: React.ComponentType;
	concepts: string[];
}

const lessons: Lesson[] = [
	{
		id: 1,
		title: "Gravity",
		subtitle: "What goes up must come down",
		icon: "🌍",
		color: "#3a86ff",
		description: "Explore how gravity accelerates objects. Compare the gravitational pull on the Moon, Mars, Earth, and Jupiter — all in real-time!",
		component: Lesson1Gravity,
		concepts: ["Gravitational acceleration (g)", "Free fall", "Terminal velocity", "Weight vs mass"],
	},
	{
		id: 2,
		title: "Elasticity",
		subtitle: "The science of bouncing",
		icon: "🏀",
		color: "#00f5d4",
		description: "Discover the coefficient of restitution — the bounciness factor. See why a rubber ball behaves differently from a clay ball.",
		component: Lesson2Elasticity,
		concepts: ["Coefficient of restitution", "Elastic vs inelastic", "Energy loss on impact", "Material properties"],
	},
	{
		id: 3,
		title: "Collisions",
		subtitle: "Momentum is always conserved",
		icon: "💥",
		color: "#ff006e",
		description: "Watch balls collide and exchange momentum. See how mass affects collision outcomes with head-on impacts and Newton's cradle.",
		component: Lesson3Collisions,
		concepts: ["Conservation of momentum", "Elastic collisions", "Mass & velocity exchange", "Newton's third law"],
	},
	{
		id: 4,
		title: "Energy",
		subtitle: "KE + PE = constant (ish)",
		icon: "⚡",
		color: "#fee440",
		description: "Visualize the dance between kinetic and potential energy with real-time energy bar graphs as balls bounce around.",
		component: Lesson5Energy,
		concepts: ["Kinetic energy (½mv²)", "Potential energy (mgh)", "Energy conservation", "Energy dissipation"],
	},
	{
		id: 5,
		title: "Sandbox",
		subtitle: "Your physics playground",
		icon: "🧪",
		color: "#8338ec",
		description: "Full control! Tweak gravity, bounciness, air resistance, and ball size. Spawn explosions and rain. Go wild.",
		component: Lesson4Sandbox,
		concepts: ["All concepts combined", "Experimentation", "Parameter tuning", "Creative exploration"],
	},
];

export function App() {
	const [activeLesson, setActiveLesson] = useState<number | null>(null);
	const [completedLessons, setCompletedLessons] = useState<Set<number>>(new Set());

	const currentLesson = lessons.find((l) => l.id === activeLesson);

	const markComplete = (id: number) => {
		setCompletedLessons((prev) => new Set([...prev, id]));
	};

	if (currentLesson) {
		const LessonComponent = currentLesson.component;
		const lessonIndex = lessons.findIndex((l) => l.id === currentLesson.id);
		const nextLesson = lessons[lessonIndex + 1];

		return (
			<div className="min-h-screen bg-[#0a0a0f] text-white">
				{/* Top bar */}
				<div className="border-b border-white/10 bg-[#0a0a0f]/80 backdrop-blur-sm sticky top-0 z-50">
					<div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
						<button
							onClick={() => setActiveLesson(null)}
							className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="rotate-180">
								<path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							Back to Course
						</button>
						<div className="flex items-center gap-3">
							<span className="text-xs text-muted-foreground">
								Lesson {lessonIndex + 1} of {lessons.length}
							</span>
							<div className="flex gap-1">
								{lessons.map((l, i) => (
									<div
										key={l.id}
										className="w-2 h-2 rounded-full transition-all"
										style={{
											backgroundColor:
												l.id === currentLesson.id
													? currentLesson.color
													: completedLessons.has(l.id)
														? "#00f5d4"
														: "#333",
										}}
									/>
								))}
							</div>
						</div>
					</div>
				</div>

				<div className="max-w-5xl mx-auto px-4 py-8">
					{/* Lesson header */}
					<div className="mb-6">
						<div className="flex items-center gap-3 mb-2">
							<span className="text-3xl">{currentLesson.icon}</span>
							<div>
								<h1 className="text-2xl font-bold" style={{ color: currentLesson.color }}>
									{currentLesson.title}
								</h1>
								<p className="text-sm text-muted-foreground">{currentLesson.subtitle}</p>
							</div>
						</div>
						<p className="text-sm text-muted-foreground max-w-2xl">{currentLesson.description}</p>
					</div>

					{/* Key concepts */}
					<div className="flex flex-wrap gap-2 mb-6">
						{currentLesson.concepts.map((concept) => (
							<span
								key={concept}
								className="px-3 py-1 rounded-full text-xs font-medium border"
								style={{
									borderColor: currentLesson.color + "40",
									color: currentLesson.color,
									backgroundColor: currentLesson.color + "10",
								}}
							>
								{concept}
							</span>
						))}
					</div>

					{/* Simulation */}
					<Card className="bg-[#111118] border-white/10">
						<CardContent className="pt-6">
							<LessonComponent />
						</CardContent>
					</Card>

					{/* Navigation */}
					<div className="flex justify-between items-center mt-6">
						<Button
							variant="outline"
							size="sm"
							onClick={() => {
								const prev = lessons[lessonIndex - 1];
								if (prev) setActiveLesson(prev.id);
							}}
							disabled={lessonIndex === 0}
							className="border-white/10"
						>
							Previous Lesson
						</Button>

						{nextLesson ? (
							<Button
								size="sm"
								onClick={() => {
									markComplete(currentLesson.id);
									setActiveLesson(nextLesson.id);
								}}
								style={{ backgroundColor: currentLesson.color }}
								className="text-black font-semibold hover:opacity-90"
							>
								Complete & Next →
							</Button>
						) : (
							<Button
								size="sm"
								onClick={() => {
									markComplete(currentLesson.id);
									setActiveLesson(null);
								}}
								style={{ backgroundColor: currentLesson.color }}
								className="text-black font-semibold hover:opacity-90"
							>
								Finish Course ✓
							</Button>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Course overview
	return (
		<div className="min-h-screen bg-[#0a0a0f] text-white">
			{/* Hero */}
			<div className="relative overflow-hidden">
				<div className="absolute inset-0 bg-gradient-to-b from-[#3a86ff10] via-transparent to-transparent" />
				<div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#8338ec08] blur-3xl" />
				<div className="absolute top-20 right-[20%] w-[300px] h-[300px] rounded-full bg-[#ff006e08] blur-3xl" />

				<div className="relative max-w-5xl mx-auto px-4 pt-16 pb-12 text-center">
					<div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs text-muted-foreground mb-6">
						<span className="w-2 h-2 rounded-full bg-[#00f5d4] animate-pulse" />
						Interactive Physics Course
					</div>

					<h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">
						<span className="bg-gradient-to-r from-[#3a86ff] via-[#8338ec] to-[#ff006e] bg-clip-text text-transparent">
							Bouncing Physics
						</span>
					</h1>

					<p className="text-lg text-muted-foreground max-w-xl mx-auto mb-2">
						Learn gravity, collisions, elasticity, and energy conservation through real-time interactive simulations.
					</p>

					<p className="text-sm text-muted-foreground/60 mb-8">
						5 lessons &middot; Hands-on &middot; No math prerequisites
					</p>

					<Button
						size="lg"
						onClick={() => setActiveLesson(1)}
						className="bg-gradient-to-r from-[#3a86ff] to-[#8338ec] text-white font-semibold hover:opacity-90 px-8 text-base"
					>
						Start Learning →
					</Button>

					{completedLessons.size > 0 && (
						<p className="text-xs text-muted-foreground mt-4">
							{completedLessons.size}/{lessons.length} lessons completed
						</p>
					)}
				</div>
			</div>

			{/* Lesson cards */}
			<div className="max-w-5xl mx-auto px-4 pb-16">
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{lessons.map((lesson, index) => {
						const isCompleted = completedLessons.has(lesson.id);
						return (
							<Card
								key={lesson.id}
								className="bg-[#111118] border-white/10 hover:border-white/20 transition-all cursor-pointer group relative overflow-hidden"
								onClick={() => setActiveLesson(lesson.id)}
							>
								{/* Glow effect */}
								<div
									className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
									style={{
										background: `radial-gradient(circle at 50% 0%, ${lesson.color}15, transparent 70%)`,
									}}
								/>

								<CardHeader className="relative">
									<div className="flex items-center justify-between mb-2">
										<span className="text-2xl">{lesson.icon}</span>
										<div className="flex items-center gap-2">
											{isCompleted && (
												<span className="text-xs px-2 py-0.5 rounded-full bg-[#00f5d4]/10 text-[#00f5d4] border border-[#00f5d4]/30">
													Completed
												</span>
											)}
											<span className="text-xs text-muted-foreground">
												{String(index + 1).padStart(2, "0")}
											</span>
										</div>
									</div>
									<CardTitle className="text-lg" style={{ color: lesson.color }}>
										{lesson.title}
									</CardTitle>
									<CardDescription>{lesson.subtitle}</CardDescription>
								</CardHeader>

								<CardContent className="relative">
									<p className="text-sm text-muted-foreground mb-4">
										{lesson.description}
									</p>
									<div className="flex flex-wrap gap-1.5">
										{lesson.concepts.slice(0, 3).map((c) => (
											<span
												key={c}
												className="text-[10px] px-2 py-0.5 rounded-full border"
												style={{
													borderColor: lesson.color + "30",
													color: lesson.color + "cc",
												}}
											>
												{c}
											</span>
										))}
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>
			</div>

			{/* Footer */}
			<div className="border-t border-white/5 py-8 text-center">
				<p className="text-xs text-muted-foreground/40">
					Built with real-time physics simulation &middot; All simulations run at 60fps in your browser
				</p>
			</div>
		</div>
	);
}

export default App;
